import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface TranscriptionData {
  isFinal: boolean;
  transcript: string;
}

@Injectable({
  providedIn: 'root'
})

export class TranscriptionService {
  private ws: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private lastChunkTs: number = 0;
  private connectCallbacks: Array<(status: number) => void> = [];
  private pendingRecordingStart: {
    resolve: () => void;
    reject: (reason?: any) => void;
    timeout: any;
  } | null = null;
  private connectionStatus = new Subject<boolean>();
  private transcriptionData = new Subject<TranscriptionData>();
  private error = new Subject<string>();
  private recordingStatus = new Subject<boolean>();
  private pausedStatus = new Subject<boolean>();
  public isConnected = false;
  private systemEvents = new Subject<any>();

  // Public observables for components to subscribe to
  public connectionStatus$: Observable<boolean> = this.connectionStatus.asObservable();
  public transcriptionData$: Observable<TranscriptionData> = this.transcriptionData.asObservable();
  public error$: Observable<string> = this.error.asObservable();
  public recordingStatus$: Observable<boolean> = this.recordingStatus.asObservable();
  public pausedStatus$: Observable<boolean> = this.pausedStatus.asObservable();
  public systemEvents$: Observable<any> = this.systemEvents.asObservable();

  constructor() { }

  getWsUrl(){
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/aiml-ws/api/ws`
      return wsUrl;
  }
  connect(callback): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      callback(1);
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      this.connectCallbacks.push(callback);
      return;
    }

    const wsUrl = this.getWsUrl();
    this.connectCallbacks = [callback];
    this.ws = new WebSocket(wsUrl);

    const notifyConnectCallbacks = (status: number) => {
      const callbacks = this.connectCallbacks.splice(0);
      callbacks.forEach(cb => {
        try { cb(status); } catch (e) { console.warn('WebSocket callback failed', e); }
      });
    };

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.isConnected = true;
      this.connectionStatus.next(true);
      notifyConnectCallbacks(1);
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        switch (message.type) {
          case 'transcription':
            this.transcriptionData.next(message.data as TranscriptionData);
            break;
          case 'recording_started':
            console.log('Recording started on server');
            this.resolvePendingRecordingStart();
            this.recordingStatus.next(true);
            break;
          case 'recording_stopped':
            console.log('Recording stopped on server');
            this.recordingStatus.next(false);
            break;
          case 'recording_paused':
            this.recordingStatus.next(false);
            this.systemEvents.next({ type: 'recording_paused', data: message.data || {} });
            break;
          case 'recording_resumed':
            this.recordingStatus.next(true);
            this.systemEvents.next({ type: 'recording_resumed', data: message.data || {} });
            break;
          case 'recording_timed_out':
            this.recordingStatus.next(false);
            this.systemEvents.next({ type: 'recording_timed_out', data: message.data || {} });
            break;
          case 'service_busy':
              console.warn('Server busy:', message.data);
              this.rejectPendingRecordingStart({
                message: message.data?.message || 'Server is busy, try again later',
                alreadyNotified: true
              });
              this.error.next(message.data?.message || 'Server is busy, try again later');
              this.recordingStatus.next(false);
              this.systemEvents.next({ type: 'service_busy', data: message.data || {} });
              break;
          case 'error':
            console.error('WebSocket error:', message.data);
            this.rejectPendingRecordingStart({
              message: message.data?.message || 'Unknown error',
              alreadyNotified: true
            });
            this.error.next(message.data?.message || 'Unknown error');
            this.recordingStatus.next(false);
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        this.error.next('Failed to parse server response');
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.isConnected = false;
      this.rejectPendingRecordingStart('WebSocket disconnected');
      this.connectionStatus.next(false);
      this.recordingStatus.next(false);
      this.systemEvents.next({ type: 'connection_closed' });
      this.pausedStatus.next(false);
      notifyConnectCallbacks(2);
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
      this.rejectPendingRecordingStart('WebSocket connection failed');
      this.connectionStatus.next(false);
      this.recordingStatus.next(false);
      this.pausedStatus.next(false);
      this.systemEvents.next({ type: 'connection_error', data: error });
      this.error.next('WebSocket connection failed');
      notifyConnectCallbacks(0);
    };
  }

  

  async startRecording(): Promise<void> {
    if (!this.isConnected || this.ws?.readyState !== WebSocket.OPEN) {
      this.error.next('WebSocket not connected');
      return Promise.reject('WebSocket not connected');
    }

    try {
      this.lastChunkTs = Date.now();
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,
          channelCount: 1
        }
      });

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 64000
      });
      let audioSeq = 0;

      this.mediaRecorder.ondataavailable = async (event) => {
        if (!(event.data && event.data.size > 0 && this.ws?.readyState === WebSocket.OPEN)) return;

        const buffer = await event.data.arrayBuffer();
        const now = Date.now();
        let durationMs = 100; // fallback timeslice ms (you used mediaRecorder.start(100))
        if (this.lastChunkTs && this.lastChunkTs > 0) {
          durationMs = Math.max(10, now - this.lastChunkTs); // clamp to avoid zero
        }
        this.lastChunkTs = now;
        const durationSec = durationMs / 1000.0;
        const header = {
          type: "audio_data",
          seq: audioSeq++,
          // optional: timestamp, sampleRate, chunkSize etc
          meta: { ts: Date.now(), bytes: buffer.byteLength,duration: durationSec }
        };

        if(this.ws){
          // 1) send metadata as a text frame (JSON)
          this.ws.send(JSON.stringify(header));
          // 2) immediately send the ArrayBuffer as a binary frame
          this.ws.send(buffer);
        }        
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.error.next('Recording failed');
        this.recordingStatus.next(false);
      };

      
      const serverStarted = this.waitForRecordingStarted();
      this.ws.send(JSON.stringify(
        { 
          type: 'start_transcription',
          data: { provider: 'google', format: 'webm_opus', sampleRate: 48000 } 
        }
      ));
      await serverStarted;
      this.mediaRecorder.start(100); // Send data every 100ms
      this.recordingStatus.next(true);
      this.pausedStatus.next(false);

    } catch (error) {
      console.error('Failed to start recording:', error);
      this.cleanupRecorderAndStream();
      this.rejectPendingRecordingStart(error);
      const message = this.getErrorMessage(error, 'Failed to access microphone');
      if (!error?.alreadyNotified && message !== 'Recording stopped' && message !== 'New recording started') {
        this.error.next(message);
      }
      return Promise.reject(message);
    }
  }

  stopRecording(): void {
    this.cleanupRecorderAndStream();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop_transcription' }));
    }
    this.rejectPendingRecordingStart('Recording stopped');
    this.recordingStatus.next(false);
    this.pausedStatus.next(false);
  }

  disconnect(): void {
    this.stopRecording();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private waitForRecordingStarted(): Promise<void> {
    this.rejectPendingRecordingStart('New recording started');
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRecordingStart = null;
        reject('Transcription start timed out');
      }, 10000);
      this.pendingRecordingStart = { resolve, reject, timeout };
    });
  }

  private resolvePendingRecordingStart(): void {
    if (!this.pendingRecordingStart) return;
    clearTimeout(this.pendingRecordingStart.timeout);
    this.pendingRecordingStart.resolve();
    this.pendingRecordingStart = null;
  }

  private rejectPendingRecordingStart(reason?: any): void {
    if (!this.pendingRecordingStart) return;
    clearTimeout(this.pendingRecordingStart.timeout);
    this.pendingRecordingStart.reject(reason);
    this.pendingRecordingStart = null;
  }

  private cleanupRecorderAndStream(): void {
    if (this.mediaRecorder) {
      try {
        if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      } catch (e) {
        console.warn('Failed to stop MediaRecorder', e);
      }
      this.mediaRecorder = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    return fallback;
  }
}
